// packages/carbon/src/no-code/pulse/floor.ts
import { shallowCap } from './shallowCap';
import type {
  ActionResult,
  DataSourcePhase,
  EntityRefLite,
  EntityType,
  ErrorSource,
  Json,
  PulseKind,
  PulseLogInput,
  PulseScope,
  RawEmit,
  ScopeId,
  StoreKey,
} from './types';

let PULSE_ON = false; // pm-004: default false, NO sessionStorage seed
// always-on error capture: independent of the full PULSE_ON gate. When true, error-severity
// events (error kind, failed actions, failed data sources) are captured even before the
// Pulse panel is mounted, so the sidebar can flag errors the maker hasn't opened DevTools for.
// Builder-only — set solely via activatePulseErrorCapture(); never true in production runtime.
let ERRORS_ON = false;
let sink: ((emit: RawEmit) => void) | undefined;
let scopeSink:
  | ((op: 'register' | 'unregister' | 'reset', value: PulseScope | ScopeId | string) => void)
  | undefined;
const snapshotProviders = new Map<ScopeId, () => unknown>();

export function setSnapshotProvider(scopeId: ScopeId, fn: () => unknown) {
  snapshotProviders.set(scopeId, fn);
}
export function clearSnapshotProvider(scopeId: ScopeId) {
  snapshotProviders.delete(scopeId);
}

// per-scope resolver from a real entity id → its human display name (block displayName,
// page-variable name, …). Registered by the store provider that owns the names; read at
// event-build time so the stream/detail can show "First name" instead of "b_sRfUe".
type EntityNameProvider = (type: EntityType, id: string) => string | undefined;
const nameProviders = new Map<ScopeId, EntityNameProvider>();

const nameCache = new Map<string, string>();
function nameCacheKey(scope: ScopeId, type: EntityType, id: string): string {
  return `${scope}:${type}:${id}`;
}

// Scans every registered scope's provider for the id, trying the event's own scope first and then
// the rest, returning the first hit. There is no scope ancestry: an id authored in any scope
// resolves wherever its provider lives, so an instance scope (m1:row_2) with no provider of its own
// still resolves via its manager base scope (m1:base) — that base is just another entry in the scan.
function scanProviders<Provider extends (...args: never[]) => string | undefined>(
  scope: ScopeId,
  providers: Map<ScopeId, Provider>,
  probe: (provider: Provider) => string | undefined,
): string | undefined {
  const own = providers.get(scope);
  if (own) {
    const hit = probe(own);
    if (hit) return hit;
  }
  for (const [scopeId, provider] of providers) {
    if (scopeId === scope) continue;
    const hit = probe(provider);
    if (hit) return hit;
  }
  return undefined;
}

export function setNameProvider(scopeId: ScopeId, fn: EntityNameProvider) {
  nameProviders.set(scopeId, fn);
}
export function clearNameProvider(scopeId: ScopeId) {
  nameProviders.delete(scopeId);
}
function resolveEntityNameLive(scope: ScopeId, type: EntityType, id: string): string | undefined {
  try {
    return scanProviders(scope, nameProviders, (provider) => provider(type, id));
  } catch {
    return undefined;
  }
}

export function resolveEntityName(
  scope: ScopeId,
  type: EntityType,
  id: string,
): string | undefined {
  const cacheKey = nameCacheKey(scope, type, id);
  const live = resolveEntityNameLive(scope, type, id);
  if (live) {
    nameCache.set(cacheKey, live);
    return live;
  }
  return nameCache.get(cacheKey);
}

// per-scope resolver from entity id -> icon name string
type EntityIconProvider = (type: string, id: string) => string | undefined;
const iconProviders = new Map<ScopeId, EntityIconProvider>();

export function setIconProvider(scopeId: ScopeId, fn: EntityIconProvider) {
  iconProviders.set(scopeId, fn);
}

export function clearIconProvider(scopeId: ScopeId) {
  iconProviders.delete(scopeId);
}

export function resolveEntityIcon(scope: ScopeId, type: string, id: string): string | undefined {
  try {
    return scanProviders(scope, iconProviders, (provider) => provider(type, id));
  } catch {
    return undefined;
  }
}

export function isPulseOn() {
  return PULSE_ON;
}
export function setPulseActive(enabled: boolean) {
  PULSE_ON = enabled;
}
export function isErrorsOn() {
  return ERRORS_ON;
}
export function setErrorsActive(enabled: boolean) {
  ERRORS_ON = enabled;
}
export function setSink(handler: (emit: RawEmit) => void) {
  sink = handler;
}
export function setScopeSink(
  handler: (op: 'register' | 'unregister' | 'reset', value: PulseScope | ScopeId | string) => void,
) {
  scopeSink = handler;
}

// kinds that can mutate the global store — we capture a state snapshot after each so the
// detail State tab can show the global state as of that event. Pure read/report kinds
// (event, error, log) get no snapshot; the State tab renders a "not captured" message.
const SNAPSHOT_KINDS = new Set<PulseKind>([
  'stateChange',
  'action',
  'componentMethod',
  'dataSource',
]);

function emit(kind: PulseKind, scope: ScopeId, raw: unknown[]) {
  let snapshot: Json | undefined;
  // capture the whole global store root for state-affecting events. Stores update immutably,
  // but shallowCap still copies so a later in-place mutation can't tear the captured value (pm-002).
  // Gated on PULSE_ON (panel open): the always-on ERRORS_ON path still records the failed
  // action/dataSource event, but must NOT deep-copy the whole store on every error while the
  // panel is closed — its State tab simply shows "not captured" for pre-open errors.
  if (PULSE_ON && SNAPSHOT_KINDS.has(kind)) {
    try {
      const root = snapshotProviders.get(scope)?.();
      snapshot = root === undefined ? undefined : (shallowCap(root) as Json);
    } catch {
      snapshot = undefined;
    }
  }
  sink?.({ kind, timestamp: Date.now(), scope, raw, snapshot });
}

export const pulse = {
  event: (
    scope: ScopeId,
    eventType: string,
    payload?: unknown,
    source?: EntityRefLite,
    label?: string,
  ) => {
    if (PULSE_ON) {
      emit('event', scope, [
        eventType,
        shallowCap(payload),
        source ? shallowCap(source) : undefined,
        label,
      ]);
    }
  },
  componentMethod: (scope: ScopeId, method: string, target: EntityRefLite, args?: unknown) => {
    if (PULSE_ON) emit('componentMethod', scope, [method, shallowCap(target), shallowCap(args)]);
  },
  action: (
    scope: ScopeId,
    actionType: string,
    result: ActionResult,
    executionType?: string,
    target?: EntityRefLite,
    trigger?: 'onSuccess' | 'onError',
    input?: unknown,
  ) => {
    // always-on path only carries failures; successful/skipped actions stay full-capture-only
    if (PULSE_ON || (ERRORS_ON && result.status === 'error')) {
      emit('action', scope, [
        actionType,
        shallowCap(result),
        executionType,
        target ? shallowCap(target) : undefined,
        trigger,
        input !== undefined ? shallowCap(input) : undefined,
      ]);
    }
  },
  stateChange: (
    scope: ScopeId,
    store: StoreKey,
    id: string,
    prev: unknown,
    next: unknown,
    instanceId?: string,
  ) => {
    if (PULSE_ON) {
      emit('stateChange', scope, [store, id, shallowCap(prev), shallowCap(next), instanceId]);
    }
  },
  dataSource: (
    scope: ScopeId,
    dsId: string,
    phase: DataSourcePhase,
    meta?: Record<string, unknown>,
  ) => {
    // always-on path only carries the error phase; request/success/skipped stay full-capture-only
    if (PULSE_ON || (ERRORS_ON && phase === 'error')) {
      emit('dataSource', scope, [
        dsId,
        phase,
        meta ? (shallowCap(meta) as Record<string, unknown>) : undefined,
      ]);
    }
  },
  error: (
    scope: ScopeId,
    source: ErrorSource,
    message: string,
    opts?: { expression?: string; stack?: string; entity?: EntityRefLite; payload?: unknown },
  ) => {
    if (PULSE_ON || ERRORS_ON) {
      emit('error', scope, [
        source,
        String(message).slice(0, 500),
        opts?.expression ? String(opts.expression).slice(0, 2000) : undefined,
        opts?.stack ? String(opts.stack).slice(0, 2000) : undefined,
        opts?.entity ? shallowCap(opts.entity) : undefined,
        opts?.payload !== undefined ? (shallowCap(opts.payload) as Json) : undefined,
      ]);
    }
  },
  log: (scope: ScopeId, input: PulseLogInput) => {
    if (PULSE_ON) {
      emit('log', scope, [
        input.label,
        shallowCap(input.payload),
        input.level,
        shallowCap(input.entity),
      ]);
    }
  },
  registerScope: (scope: PulseScope) => {
    // also fires under ERRORS_ON so the store has the display label before the panel opens
    if (PULSE_ON || ERRORS_ON) scopeSink?.('register', scope);
  },
  unregisterScope: (id: ScopeId) => {
    // ungated: a scope unregistered after Pulse is toggled off must still leave the store,
    // else it lingers as a phantom manager in the filter UI. (registerScope stays gated —
    // scopes registered while off are backfilled via replayScopes on panel activation.)
    // Deleting an absent scope is a harmless no-op.
    scopeSink?.('unregister', id);
  },
  resetScope: (managerId: string) => {
    // ungated like unregisterScope: a store torn down after Pulse is toggled off must still
    // drop its captured events, else they linger against a dead manager in the panel. When no
    // panel ever subscribed (runner, or builder before first open) there is no sink → no-op.
    scopeSink?.('reset', managerId);
  },
};

export const __resetForTest = () => {
  PULSE_ON = false;
  ERRORS_ON = false;
  sink = undefined;
  scopeSink = undefined;
  snapshotProviders.clear();
  nameProviders.clear();
  nameCache.clear();
  iconProviders.clear();
};
